import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  User as FirebaseUser,
  sendEmailVerification,
  sendPasswordResetEmail,
  verifyPasswordResetCode as verifyFirebasePasswordResetCode,
  confirmPasswordReset as confirmFirebasePasswordReset,
  updateProfile,
  updateEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  deleteUser
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { User } from '../types';

export interface AuthError {
  code: string;
  message: string;
}

export interface UserRegistrationData {
  email: string;
  password: string;
  name: string;
  role?: 'viewer' | 'editor' | 'admin';
  companyId?: string;
  teamCode?: string;
}

export interface ProfileUpdateData {
  displayName?: string;
  email?: string;
  avatarUrl?: string;
}

export interface PasswordChangeData {
  currentPassword: string;
  newPassword: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface SignupCredentials {
  email: string;
  password: string;
  name: string;
  companyName?: string;
  teamCode?: string;
}

class AuthenticationService {
  private currentUser: User | null = null;
  private authStateListeners: Array<(user: User | null) => void> = [];

  private createAuthError(code: string): Error & AuthError {
    return Object.assign(new Error(this.getErrorMessage(code)), {
      code,
      message: this.getErrorMessage(code)
    });
  }

  constructor() {
    const savedUser = localStorage.getItem('fleetfix_user');
    if (savedUser) {
      this.currentUser = JSON.parse(savedUser);
    }
    onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const user = await this.createUserFromFirebaseUser(firebaseUser);
        this.currentUser = user;
        localStorage.setItem('fleetfix_user', JSON.stringify(user));
        this.notifyAuthStateListeners(user);
      } else {
        if (this.isLocalAuthAllowed() && this.currentUser) {
          this.notifyAuthStateListeners(this.currentUser);
          return;
        }
        this.currentUser = null;
        localStorage.removeItem('fleetfix_user');
        this.notifyAuthStateListeners(null);
      }
    });
  }

  async sendPasswordReset(email: string): Promise<void> {
    try {
      await this.sendPasswordResetEmailWithFallback(email);
    } catch (error: any) {
      console.error('Password reset error:', error);
      throw this.createAuthError(error.code);
    }
  }

  
  async resendEmailVerification(): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No user logged in');
      }
      await sendEmailVerification(user);
    } catch (error: any) {
      console.error('Email verification error:', error);
      throw this.createAuthError(error.code);
    }
  }

  
  async updateProfile(profileData: ProfileUpdateData): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No user logged in');
      }

      
      if (profileData.displayName) {
        await updateProfile(user, {
          displayName: profileData.displayName
        });
      }

      
      if (profileData.email && profileData.email !== user.email) {
        await updateEmail(user, profileData.email);
        await sendEmailVerification(user);
      }

      
      if (this.currentUser) {
        this.currentUser = {
          ...this.currentUser,
          name: profileData.displayName || this.currentUser.name,
          email: profileData.email || this.currentUser.email
        };
        // Notify listeners that the user profile has been updated
        this.notifyAuthStateListeners(this.currentUser);
      }
    } catch (error: any) {
      console.error('Profile update error:', error);
      throw this.createAuthError(error.code);
    }
  }

  
  async changePassword(passwordData: PasswordChangeData): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user || !user.email) {
        throw new Error('No user logged in');
      }

      
      const credential = EmailAuthProvider.credential(user.email, passwordData.currentPassword);
      await reauthenticateWithCredential(user, credential);

      
      await updatePassword(user, passwordData.newPassword);
    } catch (error: any) {
      console.error('Password change error:', error);
      throw this.createAuthError(error.code);
    }
  }

  private async createUserFromFirebaseUser(firebaseUser: FirebaseUser): Promise<User> {
    
    console.log('Creating user object from Firebase user:', firebaseUser.uid);
    const email = firebaseUser.email || '';
    const teamClaim = this.getTeamClaimForEmail(email);
    const role = this.mapTeamRole(teamClaim?.role) || 'viewer';
    
    return {
      id: firebaseUser.uid,
      email,
      name: firebaseUser.displayName || 'User',
      role,
      companyId: teamClaim?.workspaceUserId || firebaseUser.uid,
      isActive: true,
      lastLogin: new Date(),
      createdAt: new Date(),
      preferences: {
        theme: 'light',
        notifications: {
          inApp: true
        },
        language: 'en',
        timezone: 'America/New_York'
      }
    };
  }

  private notifyAuthStateListeners(user: User | null) {
    this.authStateListeners.forEach(listener => listener(user));
  }

  
  async login(credentials: LoginCredentials): Promise<{ user: User } | { error: AuthError }> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, credentials.email, credentials.password);
      const user = await this.createUserFromFirebaseUser(userCredential.user);
      this.currentUser = user;
      localStorage.setItem('fleetfix_user', JSON.stringify(user));
      this.authStateListeners.forEach(callback => callback(user));
      return { user };
    } catch (error: any) {
      if (this.isLocalAuthAllowed()) {
        const user = this.createLocalUser(credentials.email);
        this.currentUser = user;
        localStorage.setItem('fleetfix_user', JSON.stringify(user));
        this.authStateListeners.forEach(callback => callback(user));
        return { user };
      }
      return { error: { code: error.code, message: this.getErrorMessage(error.code) } };
    }
  }

  async signup(credentials: SignupCredentials): Promise<{ user: User } | { error: AuthError }> {
    try {
      console.log('Starting signup process...');

      
      if (credentials.teamCode) {
        const validTeamCodes = (process.env.REACT_APP_TEAM_CODES || '')
          .split(',')
          .map(code => code.trim())
          .filter(Boolean);

        if (!validTeamCodes.includes(credentials.teamCode)) {
          return {
            error: {
              code: 'invalid-team-code',
              message: 'Invalid team code. Please contact your team administrator.'
            }
          };
        }
      }

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        credentials.email,
        credentials.password
      );

      
      await updateProfile(userCredential.user, {
        displayName: credentials.name
      });

      
      let userRole: 'viewer' | 'editor' | 'admin' = 'viewer';
      if (credentials.teamCode) {
        if (credentials.teamCode.includes('ADMIN')) {
          userRole = 'admin';
        } else if (credentials.teamCode.includes('MECHANIC') || credentials.teamCode.includes('TEAM')) {
          userRole = 'editor';
        }
      }

      
      const userData: Partial<User> = {
        id: userCredential.user.uid,
        email: credentials.email,
        name: credentials.name,
        role: userRole,
        companyId: this.getTeamClaimForEmail(credentials.email)?.workspaceUserId || userCredential.user.uid,
        isActive: true,
        createdAt: new Date(),
        preferences: {
          theme: 'light',
          notifications: {
            inApp: true
          },
          language: 'en',
          timezone: 'America/New_York'
        }
      };

      try {
        console.log('Creating Firestore document...');
        await Promise.race([
          setDoc(doc(db, 'users', userCredential.user.uid), userData),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Firestore timeout')), 5000))
        ]);
        console.log('Firestore document created successfully');
      } catch (firestoreError) {
        console.warn('Firestore document creation failed, continuing anyway:', firestoreError);
        
      }

      
      const user = await this.createUserFromFirebaseUser(userCredential.user);
      console.log('User creation completed successfully:', user);
      
      
      this.currentUser = user;
      this.authStateListeners.forEach(callback => callback(user));
      
      return { user };
    } catch (error: any) {
      console.error('Firebase signup error:', error);
      if (this.isLocalAuthAllowed()) {
        const user = this.createLocalUser(credentials.email, credentials.name);
        this.currentUser = user;
        localStorage.setItem('fleetfix_user', JSON.stringify(user));
        this.authStateListeners.forEach(callback => callback(user));
        return { user };
      }
      
      return { 
        error: { 
          code: error.code, 
          message: this.getErrorMessage(error.code) 
        } 
      };
    }
  }

  async logout(): Promise<void> {
    try {
      if (auth.currentUser) {
        await signOut(auth);
      }
      this.currentUser = null;
      localStorage.removeItem('fleetfix_user');
      this.authStateListeners.forEach(callback => callback(null));
    } catch (error) {
      throw error;
    }
  }

  async resetPassword(email: string): Promise<{ success: boolean; usedFallbackResetPage?: boolean } | { error: AuthError }> {
    try {
      const result = await this.sendPasswordResetEmailWithFallback(email);
      return { success: true, usedFallbackResetPage: result.usedFallbackResetPage };
    } catch (error: any) {
      console.error('Password reset email failed:', error);
      return { 
        error: { 
          code: error.code, 
          message: this.getErrorMessage(error.code) 
        } 
      };
    }
  }

  async verifyPasswordResetCode(actionCode: string): Promise<{ email: string } | { error: AuthError }> {
    try {
      const email = await verifyFirebasePasswordResetCode(auth, actionCode);
      return { email };
    } catch (error: any) {
      return {
        error: {
          code: error.code,
          message: this.getErrorMessage(error.code)
        }
      };
    }
  }

  async confirmPasswordReset(actionCode: string, newPassword: string): Promise<{ success: boolean } | { error: AuthError }> {
    try {
      await confirmFirebasePasswordReset(auth, actionCode, newPassword);
      return { success: true };
    } catch (error: any) {
      return {
        error: {
          code: error.code,
          message: this.getErrorMessage(error.code)
        }
      };
    }
  }

  async updateUserData(userId: string, data: Partial<User>): Promise<void> {
    try {
      const userDocRef = doc(db, 'users', userId);
      await setDoc(userDocRef, data, { merge: true });
    } catch (error) {
      console.error('Error updating user data:', error);
      throw error;
    }
  }

  async deleteAccount(): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('No authenticated user found');
    }

    try {
      await deleteUser(auth.currentUser);
      this.currentUser = null;
      localStorage.removeItem('authToken');
      this.notifyAuthStateListeners(null);
    } catch (error) {
      console.error('Error deleting account:', error);
      throw error;
    }
  }

  
  getCurrentUser(): User | null {
    return this.currentUser;
  }

  isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  onAuthStateChange(callback: (user: User | null) => void): () => void {
    this.authStateListeners.push(callback);
    
    
    callback(this.currentUser);
    
    
    return () => {
      this.authStateListeners = this.authStateListeners.filter(
        listener => listener !== callback
      );
    };
  }

  private getErrorMessage(errorCode: string): string {
    switch (errorCode) {
      case 'auth/user-not-found':
        return 'No account found with this email address.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      case 'auth/email-already-in-use':
        return 'An account with this email already exists.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters long.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/user-disabled':
        return 'This account has been disabled.';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection.';
      case 'auth/expired-action-code':
        return 'This password reset link has expired. Request a new link and try again.';
      case 'auth/invalid-action-code':
        return 'This password reset link is invalid or has already been used.';
      case 'auth/missing-email':
        return 'Please enter the email address for your account.';
      case 'auth/unauthorized-continue-uri':
      case 'auth/invalid-continue-uri':
      case 'auth/missing-continue-uri':
        return 'FleetFix needs this domain authorized in Firebase before password reset emails can use the in-app reset screen.';
      default:
        return 'An error occurred. Please try again.';
    }
  }

  private async sendPasswordResetEmailWithFallback(email: string): Promise<{ usedFallbackResetPage: boolean }> {
    const actionSettings = this.getPasswordResetActionSettings(email);

    try {
      if (actionSettings) {
        await sendPasswordResetEmail(auth, email, actionSettings);
      } else {
        await sendPasswordResetEmail(auth, email);
      }

      return { usedFallbackResetPage: false };
    } catch (error: any) {
      if (!actionSettings || !this.isPasswordResetReturnUrlError(error?.code)) {
        throw error;
      }

      console.warn('Password reset return URL was rejected by Firebase. Retrying with Firebase hosted reset page.', {
        code: error.code,
        resetUrl: actionSettings.url
      });
      await sendPasswordResetEmail(auth, email);
      return { usedFallbackResetPage: true };
    }
  }

  private isPasswordResetReturnUrlError(errorCode?: string): boolean {
    return [
      'auth/unauthorized-continue-uri',
      'auth/invalid-continue-uri',
      'auth/missing-continue-uri'
    ].includes(errorCode || '');
  }

  private getPasswordResetActionSettings(email: string) {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const resetOrigin = this.getPasswordResetOrigin();
    const url = new URL('/login', resetOrigin);
    url.searchParams.set('email', email.trim().toLowerCase());

    return {
      url: url.toString(),
      handleCodeInApp: true
    };
  }

  private getPasswordResetOrigin(): string {
    const configuredOrigin = process.env.REACT_APP_PUBLIC_APP_URL?.trim().replace(/\/+$/, '');
    if (configuredOrigin) {
      return configuredOrigin;
    }

    const { hostname, port, protocol, origin } = window.location;
    if (hostname === '127.0.0.1' || hostname === '::1') {
      return `${protocol}//localhost${port ? `:${port}` : ''}`;
    }

    if (hostname.endsWith('.vercel.app')) {
      return 'https://fleetfix.app';
    }

    return origin;
  }

  private getTeamClaimForEmail(email: string): { workspaceUserId: string; role: string } | null {
    if (!email || typeof localStorage === 'undefined') return null;

    try {
      const key = `fleetfix_team_claim_${email.trim().toLowerCase()}`;
      const rawClaim = localStorage.getItem(key);
      return rawClaim ? JSON.parse(rawClaim) : null;
    } catch (error) {
      console.error('Error loading team invite claim:', error);
      return null;
    }
  }

  private mapTeamRole(role?: string): User['role'] | null {
    if (role === 'admin') return 'admin';
    if (role === 'editor' || role === 'mechanic' || role === 'dispatcher') return 'editor';
    if (role === 'viewer' || role === 'driver') return 'viewer';
    return null;
  }

  private isLocalAuthAllowed(): boolean {
    if (typeof window === 'undefined') return false;
    return ['localhost', '127.0.0.1'].includes(window.location.hostname);
  }

  private createLocalUser(email: string, name?: string): User {
    const normalizedEmail = email.trim().toLowerCase();
    const teamClaim = this.getTeamClaimForEmail(normalizedEmail);
    const id = `local-${btoa(normalizedEmail).replace(/[^a-zA-Z0-9]/g, '').slice(0, 22)}`;
    const displayName = name?.trim() || normalizedEmail.split('@')[0] || 'Fleet Manager';

    return {
      id,
      email: normalizedEmail,
      name: displayName,
      role: this.mapTeamRole(teamClaim?.role) || 'admin',
      companyId: teamClaim?.workspaceUserId || id,
      isActive: true,
      lastLogin: new Date(),
      createdAt: new Date(),
      preferences: {
        theme: 'light',
        notifications: {
          inApp: true
        },
        language: 'en',
        timezone: 'America/New_York'
      }
    };
  }
}


export const authService = new AuthenticationService();
export default authService;
